import React, { useState, useEffect, useRef } from 'react';
import {
  connectionIdOrImportsSuggestAmazonInventory,
  connectionIdOrImportsSuggestShopifyInventory,
  isInventorySnapshotConnectionId,
} from '@/shared/inventoryCsvConnectionChannels';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StandardizedConnection } from '@/types/connections';
import { useConnectorState } from '@/hooks/useConnectorState';
import ConnectorModalContent from './ConnectorModalContent';
import { usePlaidLink } from 'react-plaid-link';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { draftTokenField } from '@/utils/onboarding/draftToken';
import { toast } from 'sonner';

interface ConnectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedConnectors: StandardizedConnection[]) => void;
  linkedConnectors: StandardizedConnection[];
  onRemoveConnector: (connectorId: string,connectionId:string) => Promise<void>;
  onConnectStart?: () => void;
  connectorId?: string;
  isLoadingConnections?: boolean;
  isLoadingAfterRedirect?: boolean;
  onRefreshLinkedConnectors?: () => Promise<void>;
  onBeginConnectionsRefresh?: () => void;
  organisationId?: string;
}

const ConnectorModal: React.FC<ConnectorModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  linkedConnectors,
  onRemoveConnector,
  onConnectStart,
  connectorId,
  isLoadingConnections = false,
  isLoadingAfterRedirect = false,
  onRefreshLinkedConnectors,
  onBeginConnectionsRefresh,
  organisationId: organisationIdProp
}) => {
  const {
    view,
    setView,
    connectors,
    loading,
    selectedConnector,
    setSelectedConnector,
    error,
    isConnecting,
    shopName,
    setShopName,
    merchantId,
    setMerchantId,
    isVendor,
    setIsVendor,
    loadConnectors,
    handleConnect
  } = useConnectorState(connectorId);

  const [plaidReauthToken, setPlaidReauthToken] = useState<string | null>(null);
  const [reauthConnectorId, setReauthConnectorId] = useState<string | null>(null);

  const plaidEnv = typeof window !== 'undefined' && window.location.host === 'onboarding.figabl.com' ? 'production' : 'sandbox';

  const { open: openPlaidReauth, ready: plaidReauthReady } = usePlaidLink({
    env: plaidEnv,
    token: plaidReauthToken,
    onSuccess: async (public_token, metadata) => {
      if (!reauthConnectorId) return;
      onBeginConnectionsRefresh?.();
      setView('initial');
      try {
        const organisationId = organisationIdProp ?? (() => {
          const saved = sessionStorage.getItem('figabl_company_data');
          if (!saved) return undefined;
          const parsed = JSON.parse(saved) as { organization_id?: string; organisation_id?: string };
          return parsed.organization_id || parsed.organisation_id;
        })();
        const { data, error } = await invokeFunction(supabase, 'plaid-exchange-token', {
          body: {
            public_token,
            metadata,
            organisationId,
            isUpdateMode: true,
            existingConnectionId: reauthConnectorId,
            ...draftTokenField(organisationId),
          },
        });
        if (error) throw error;
        if (data && data.success === false) {
          throw new Error(data.message || 'Re-authentication failed');
        }
        await onRefreshLinkedConnectors?.();
        toast.success('Bank connection re-authenticated');
      } catch (err) {
        console.error('[PlaidReauth] Error completing reauth:', err);
        toast.error('Failed to re-authenticate bank connection');
      } finally {
        setPlaidReauthToken(null);
        setReauthConnectorId(null);
      }
    },
    onExit: () => {
      setPlaidReauthToken(null);
      setReauthConnectorId(null);
    },
    onEvent: (e) => console.log('[PlaidReauth]', e),
  });

  useEffect(() => {
    if (plaidReauthToken && plaidReauthReady) {
      openPlaidReauth();
    }
  }, [plaidReauthToken, plaidReauthReady, openPlaidReauth]);

  const handlePlaidReauth = async (connector: StandardizedConnection) => {
    if (connector.metadata?.has_access_token === false || connector.metadata?.connection_data_lost === true) {
      toast.error('Bank connection data lost, remove and connect again');
      return;
    }
    const connId = connector.id;
    const organisationId = organisationIdProp ?? (() => {
      try {
        const saved = sessionStorage.getItem('figabl_company_data');
        if (!saved) return undefined;
        const parsed = JSON.parse(saved) as { organization_id?: string; organisation_id?: string };
        return parsed.organization_id || parsed.organisation_id;
      } catch {
        return undefined;
      }
    })();
    try {
      const { data, error } = await invokeFunction(supabase, 'plaid-create-link-token', {
        body: { connectionId: connId, organisationId },
      });
      if (error || !data?.link_token) throw error || new Error('No link token');
      setReauthConnectorId(connId);
      setPlaidReauthToken(data.link_token);
    } catch (err) {
      console.error('[PlaidReauth] Error creating link token:', err);
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to start bank re-authentication');
    }
  };

  const onRefreshLinkedConnectorsRef = useRef(onRefreshLinkedConnectors);
  onRefreshLinkedConnectorsRef.current = onRefreshLinkedConnectors;

  useEffect(() => {
    if (!isOpen) {
      setView('initial');
      return;
    }
    // Refresh once per open — do not depend on the callback identity (loadAllConnections
    // is recreated each parent render and would retrigger this effect in a loop).
    void onRefreshLinkedConnectorsRef.current?.();
  }, [isOpen, setView]);

  const handleFileOption = () => {
    if (onConnectStart) {
      onConnectStart();
    }
    setView('file');
  };

  const handleDatabaseOption = () => {
    setView('database');
  };

  const handleConnectionSuccess = async (newConnector: StandardizedConnection) => {
    if (newConnector.service === 'plaid' && onRefreshLinkedConnectors) {
      onBeginConnectionsRefresh?.();
      setView('initial');
      // The refresh only reloads ids this tab already owns. Add the bank first,
      // or the next draft save overwrites the organisation without it.
      onSuccess([...linkedConnectors, newConnector]);
      await onRefreshLinkedConnectors();
      return;
    }

    const updatedConnectors = [...linkedConnectors];
    
    // Check if this is a CSV import that should be grouped
    const isCsvImport = newConnector.service === 'csv' || 
                        newConnector.type === 'csv' ||
                        newConnector.type === 'CSV' ||
                        newConnector.connectorId?.startsWith('csv-import-');
    
    if (isCsvImport) {
      // Find existing CSV connection with same import_type and ecommerce_subtype
      let existingCsvIndex = updatedConnectors.findIndex(conn =>
        (conn.service === 'csv' || conn.type === 'csv' || conn.type === 'CSV') &&
        conn.import_type === newConnector.import_type &&
        conn.ecommerce_subtype === newConnector.ecommerce_subtype
      );

      // Do not merge inventory snapshot uploads for different channels under one parent
      // (parent ecommerce_subtype can be wrong if the second upload reused stale subtype state).
      if (existingCsvIndex >= 0) {
        const newAmazon = connectionIdOrImportsSuggestAmazonInventory(newConnector as Record<string, unknown>);
        const newShopify = connectionIdOrImportsSuggestShopifyInventory(newConnector as Record<string, unknown>);
        if (newAmazon || newShopify) {
          const existingRow = updatedConnectors[existingCsvIndex] as Record<string, unknown>;
          const existingAmazon = connectionIdOrImportsSuggestAmazonInventory(existingRow);
          const existingShopify = connectionIdOrImportsSuggestShopifyInventory(existingRow);
          if ((newAmazon && existingShopify) || (newShopify && existingAmazon)) {
            existingCsvIndex = -1;
          }
        }
      }

      if (existingCsvIndex >= 0) {
        // Group with existing CSV connection
        const existingCsv = updatedConnectors[existingCsvIndex];
        
        // Initialize csvImports array if not present
        const existingImports = existingCsv.csvImports || [];
        
        // If there's no csvImports but there's a file_name, add it first
        if (existingImports.length === 0 && existingCsv.file_name) {
          const exSub = String(existingCsv.ecommerce_subtype || '').toLowerCase();
          existingImports.push({
            id: existingCsv.id,
            date: existingCsv.created_at || new Date().toISOString(),
            fileName: existingCsv.file_name,
            snapshot_connection_id: existingCsv.metadata?.snapshot_connection_id,
            snapshot_date: existingCsv.metadata?.snapshot_date || existingCsv.metadata?.inventory_date,
            inventory_source: exSub === 'shopify'
              ? 'shopify_csv_import'
              : exSub === 'amazon'
                ? 'amazon_csv_import'
                : undefined,
            csv_import_job_id: existingCsv.metadata?.csv_import_job_id,
          });
        }

        const newSub = String(newConnector.ecommerce_subtype || '').toLowerCase();
        // Add new import to the array
        existingImports.push({
          id: newConnector.id,
          date: new Date().toISOString(),
          fileName: newConnector.file_name || newConnector.metadata?.file_name || 'Unnamed',
          snapshot_connection_id: newConnector.metadata?.snapshot_connection_id,
          snapshot_date: newConnector.metadata?.snapshot_date || newConnector.metadata?.inventory_date,
          inventory_source: newSub === 'shopify'
            ? 'shopify_csv_import'
            : newSub === 'amazon'
              ? 'amazon_csv_import'
              : undefined,
          csv_import_job_id: newConnector.metadata?.csv_import_job_id,
        });
        
        // Update the existing connection with the new csvImports
        updatedConnectors[existingCsvIndex] = {
          ...existingCsv,
          name: "CSV Import",
          type: "CSV",
          csvImports: existingImports
        };
      } else {
        // First CSV of this type - create new connection with csvImports array
        const isInventorySnapshotConnector = isInventorySnapshotConnectionId(newConnector.id);
        const csvConnectionId = isInventorySnapshotConnector
          ? String(newConnector.id)
          : (newConnector.connectionId || newConnector.id);
        const baseSessionId =
          newConnector.connectionId ||
          newConnector.connectorId ||
          csvConnectionId;
        const csvConnection: StandardizedConnection = {
          id: csvConnectionId,
          name: "CSV Import",
          status: 'connected',
          logoUrl: newConnector.logoUrl || "https://via.placeholder.com/40?text=CSV",
          service: 'csv',
          connectorId: isInventorySnapshotConnector ? baseSessionId : csvConnectionId,
          description: `CSV file: ${newConnector.file_name || 'Unnamed'}`,
          connectionId: isInventorySnapshotConnector ? baseSessionId : csvConnectionId,
          type: 'CSV',
          created_at: new Date().toISOString(),
          import_type: newConnector.import_type,
          ecommerce_subtype: newConnector.ecommerce_subtype,
          csvImports: [{
            id: newConnector.id,
            date: new Date().toISOString(),
            fileName: newConnector.file_name || newConnector.metadata?.file_name || 'Unnamed',
            ...(newConnector.metadata?.snapshot_connection_id && {
              snapshot_connection_id: newConnector.metadata.snapshot_connection_id
            }),
            ...((newConnector.metadata?.snapshot_date || newConnector.metadata?.inventory_date) && {
              snapshot_date: newConnector.metadata?.snapshot_date || newConnector.metadata?.inventory_date
            }),
            ...(String(newConnector.ecommerce_subtype || '').toLowerCase() === 'shopify' && {
              inventory_source: 'shopify_csv_import' as const
            }),
            ...(String(newConnector.ecommerce_subtype || '').toLowerCase() === 'amazon' && {
              inventory_source: 'amazon_csv_import' as const
            }),
            ...(newConnector.metadata?.csv_import_job_id && {
              csv_import_job_id: newConnector.metadata.csv_import_job_id
            }),
          }]
        };
        updatedConnectors.push(csvConnection);
      }
    } else {
      // Non-CSV connector - add as-is
      updatedConnectors.push(newConnector);
    }
    
    onSuccess(updatedConnectors);
    setView('initial');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <ConnectorModalContent
          view={view}
          loading={loading || isLoadingConnections}
          error={error}
          connectors={connectors}
          selectedConnector={selectedConnector}
          isConnecting={isConnecting}
          linkedConnectors={linkedConnectors}
          onFileOption={handleFileOption}
          onDatabaseOption={handleDatabaseOption}
          onBack={() => setView('initial')}
          onRemoveConnector={onRemoveConnector}
          onConnectorSelect={setSelectedConnector}
          onConnect={handleConnect}
          onConnectionSuccess={handleConnectionSuccess}
          onClose={onClose}
          onLoadConnectors={loadConnectors}
          isLoadingConnections={isLoadingConnections}
          isLoadingAfterRedirect={isLoadingAfterRedirect}
          shopName={shopName}
          onShopNameChange={setShopName}
          merchantId={merchantId}
          onMerchantIdChange={setMerchantId}
          isVendor={isVendor}
          onIsVendorChange={setIsVendor}
          onPlaidReauth={handlePlaidReauth}
          onBeginConnectionsRefresh={onBeginConnectionsRefresh}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ConnectorModal;
